import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApiService } from '../../services/api';
import { Mail, Lock, ArrowRight, Sparkles, Phone, MapPin, MessageCircle } from 'lucide-react';
import ledgerGlideLogo from '../../assets/croped logo.png';

const SignInPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { requestPasswordReset } = useApiService();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sign-in-container">
      {/* Background decorative elements */}
      <div className="bg-decoration">
        <div className="bg-circle bg-circle-1"></div>
        <div className="bg-circle bg-circle-2"></div>
        <div className="bg-circle bg-circle-3"></div>
      </div>

      {/* Left side - Branding */}
      <div className="sign-in-left">
          <div className="brand-content">
            <div className="logo-wrapper">
              <img src={ledgerGlideLogo} alt="Ledger Glide" className="brand-logo" />
              {/* <h4 className="brand-title-inline"></h4> */}

              <p className="brand-subtitle-inline">
              Streamline your showroom operations with powerful management tools
            </p>
            </div>
            
          <div className="feature-list">
            <div className="feature-item">
              <Sparkles size={20} className="feature-icon" />
              <span>All Digital Records</span>
            </div>
            <div className="feature-item">
              <Sparkles size={20} className="feature-icon" />
              <span>Powerful Reporting Tools</span>
            </div>
            <div className="feature-item">
              <Sparkles size={20} className="feature-icon" />
              <span>Deals, Payment Structure, Payment Calulations & Much More</span>
            </div>
          </div>

          {/* Contact Section */}
          <div className="contact-section">
            <h3 className="contact-title">Get in Touch</h3>
            <div className="contact-item">
              <div className="contact-icon-wrapper">
                <MessageCircle size={18} className="contact-icon" />
              </div>
              <div className="contact-info">
                <span className="contact-label">Contact & WhatsApp</span>
                <a href="https://wa.me/923332416545" target="_blank" rel="noopener noreferrer" className="contact-value">
                  +923332416545
                </a>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon-wrapper">
                <MapPin size={18} className="contact-icon" />
              </div>
              <div className="contact-info">
                <span className="contact-label">Address</span>
                <span className="contact-value">FastFly, Shahra e Faisal, Karachi</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="sign-in-right">
        <div className="sign-in-card">
          <div className="sign-in-header">
            <h2>Welcome Back</h2>
            <p>Sign in to continue to your account</p>
          </div>

          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 6V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="sign-in-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Enter your password"
                />
              </div>
              <div className="forgot-password-wrapper">
                <button
                  type="button"
                  onClick={() => {
                    setShowReset(true);
                    setResetEmail(formData.email);
                    setResetMessage('');
                  }}
                  className="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="sign-in-button"
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {showReset && (
        <div className="modal-overlay" onClick={() => {
          setShowReset(false);
          setResetEmail('');
          setResetMessage('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button
                type="button"
                onClick={() => {
                  setShowReset(false);
                  setResetEmail('');
                  setResetMessage('');
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <p className="modal-description">
              Enter your account email. We'll send a new temporary password if it exists.
            </p>

            {resetMessage && (
              <div className={`reset-message ${resetMessage.includes('Failed') ? 'error' : 'success'}`}>
                {resetMessage}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="resetEmail" className="form-label">Email</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="form-input"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowReset(false);
                  setResetEmail('');
                  setResetMessage('');
                }}
                className="modal-button secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resetSubmitting}
                onClick={async () => {
                  setResetSubmitting(true);
                  setResetMessage('');
                  try {
                    await requestPasswordReset(resetEmail);
                    setResetMessage('If that email exists, a new password has been emailed.');
                  } catch (err) {
                    setResetMessage(err.message || 'Failed to send new password.');
                  } finally {
                    setResetSubmitting(false);
                  }
                }}
                className="modal-button primary"
              >
                {resetSubmitting ? 'Sending...' : 'Send New Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .sign-in-container {
          min-height: 100vh;
          display: flex;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .sign-in-container::before {
          content: '';
          position: absolute;
          inset: 0;
          background: 
            radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
          pointer-events: none;
          z-index: 0;
        }

        .bg-decoration {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .bg-circle {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          animation: float 20s infinite ease-in-out;
        }

        .bg-circle-1 {
          width: 400px;
          height: 400px;
          top: -100px;
          right: -100px;
          animation-delay: 0s;
        }

        .bg-circle-2 {
          width: 300px;
          height: 300px;
          bottom: -50px;
          left: -50px;
          animation-delay: 5s;
        }

        .bg-circle-3 {
          width: 200px;
          height: 200px;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: 10s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        .sign-in-left {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        .sign-in-left::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 1px;
          height: 100%;
          background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.2), transparent);
          z-index: 2;
        }

        .brand-content {
          max-width: 500px;
          color: white;
        }

        .logo-wrapper {
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          justify-content: flex-start;
          position: relative;
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }

        .brand-logo {
          height: 70px;
          width: auto;
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
          border-radius: 12px;
          background: transparent;
          display: block;
          flex-shrink: 0;
          animation: logoFloat 3s ease-in-out infinite;
        }

        @keyframes logoFloat {
          0%, 100% { 
            transform: translateY(0px) scale(1);
            filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
          }
          50% { 
            transform: translateY(-8px) scale(1.02);
            filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.3));
          }
        }

        .brand-title-inline {
          font-size: 2.25rem;
          font-weight: 800;
          margin: 0;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          white-space: nowrap;
        }

        .brand-title {
          font-size: 3rem;
          font-weight: 800;
          margin: 0 0 1rem 0;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .brand-subtitle {
          font-size: 1.125rem;
          line-height: 1.6;
          margin: 0 0 2.5rem 0;
          opacity: 0.95;
          color: rgba(255, 255, 255, 0.9);
        }

        .feature-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.95);
        }

        .feature-icon {
          color: rgba(255, 255, 255, 0.9);
          flex-shrink: 0;
        }

        .contact-section {
          margin-top: 2.5rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .contact-title {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0 0 1.25rem 0;
          color: rgba(255, 255, 255, 0.95);
          letter-spacing: -0.01em;
        }

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          transition: all 0.3s ease;
        }

        .contact-item:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateX(4px);
        }

        .contact-item:last-child {
          margin-bottom: 0;
        }

        .contact-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 10px;
          flex-shrink: 0;
        }

        .contact-icon {
          color: rgba(255, 255, 255, 0.95);
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          flex: 1;
        }

        .contact-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.7);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .contact-value {
          font-size: 0.9375rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contact-value:hover {
          color: #ffffff;
          text-decoration: underline;
        }

        .sign-in-right {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        .sign-in-right::before {
          content: '';
          position: absolute;
          inset: 0;
          background: 
            linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%),
            radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 70%);
          backdrop-filter: blur(40px);
          z-index: -1;
        }

        .sign-in-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 32px;
          padding: 3rem;
          width: 100%;
          max-width: 480px;
          box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.25),
            0 0 0 1px rgba(255, 255, 255, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
          animation: slideIn 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .sign-in-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
          pointer-events: none;
        }

        .sign-in-card::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -30%;
          width: 150%;
          height: 150%;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%);
          pointer-events: none;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .sign-in-header {
          text-align: center;
          margin-bottom: 2.5rem;
          position: relative;
          z-index: 1;
        }

        .sign-in-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          letter-spacing: -0.02em;
        }

        .sign-in-header p {
          color: #6b7280;
          font-size: 0.9375rem;
          margin: 0;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          animation: shake 0.3s ease-in-out;
          position: relative;
          z-index: 1;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .sign-in-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: #9ca3af;
          pointer-events: none;
          z-index: 2;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 3.25rem;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 0.9375rem;
          transition: all 0.2s ease;
          background: #ffffff;
          box-sizing: border-box;
          color: #1f2937;
        }

        .form-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .form-input::placeholder {
          color: #9ca3af;
        }

        .form-input:-webkit-autofill,
        .form-input:-webkit-autofill:hover,
        .form-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0px 1000px white inset;
          -webkit-text-fill-color: #1f2937;
          padding-left: 3.25rem;
        }

        .forgot-password-wrapper {
          display: flex;
          justify-content: flex-end;
          margin-top: 0.5rem;
        }

        .forgot-password-btn {
          background: none;
          border: none;
          padding: 0;
          color: #6366f1;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .forgot-password-btn:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        .sign-in-button {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .sign-in-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
        }

        .sign-in-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .sign-in-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .button-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 9999;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: #ffffff;
          border-radius: 20px;
          padding: 2rem;
          width: 100%;
          max-width: 440px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .modal-header h3 {
          margin: 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 2rem;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-description {
          color: #6b7280;
          font-size: 0.9375rem;
          margin: 0 0 1.5rem 0;
          line-height: 1.5;
        }

        .reset-message {
          padding: 0.875rem 1rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }

        .reset-message.success {
          background: #ecfdf3;
          border: 1px solid #bbf7d0;
          color: #15803d;
        }

        .reset-message.error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-top: 1.5rem;
        }

        .modal-button {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-size: 0.9375rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .modal-button.secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-button.secondary:hover {
          background: #e5e7eb;
        }

        .modal-button.primary {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .modal-button.primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
        }

        .modal-button.primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 968px) {
          .sign-in-container {
            flex-direction: column;
          }

          .sign-in-left {
            padding: 2rem 1.5rem;
            min-height: auto;
          }

          .brand-title {
            font-size: 2rem;
          }

          .brand-subtitle {
            font-size: 1rem;
            margin-bottom: 1.5rem;
          }

          .feature-list {
            gap: 0.75rem;
          }

          .feature-item {
            font-size: 0.9375rem;
          }

          .contact-section {
            margin-top: 2rem;
            padding-top: 1.5rem;
          }

          .contact-title {
            font-size: 1rem;
          }

          .contact-item {
            padding: 0.625rem;
          }

          .contact-value {
            font-size: 0.875rem;
          }

          .sign-in-right {
            padding: 1.5rem;
            width: 100%;
          }

          .sign-in-right::before {
            border-radius: 0;
          }

          .sign-in-card {
            padding: 2rem;
            max-width: 100%;
            border-radius: 24px 24px 0 0;
          }
        }

        @media (max-width: 640px) {
          .logo-wrapper {
            padding: 0.5rem 1rem;
            border-radius: 12px;
            gap: 1rem;
          }

          .brand-logo {
            height: 50px;
            border-radius: 8px;
          }

          .brand-title-inline {
            font-size: 1.5rem;
          }

          .brand-title {
            font-size: 1.75rem;
          }

          .sign-in-card {
            padding: 1.5rem;
            border-radius: 20px;
          }

          .sign-in-header h2 {
            font-size: 1.75rem;
          }

          .contact-section {
            margin-top: 1.5rem;
            padding-top: 1.25rem;
          }

          .contact-item {
            padding: 0.625rem;
            gap: 0.75rem;
          }

          .contact-icon-wrapper {
            width: 32px;
            height: 32px;
          }

          .contact-icon {
            width: 16px;
            height: 16px;
          }

          .contact-label {
            font-size: 0.6875rem;
          }

          .contact-value {
            font-size: 0.8125rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SignInPage;
